import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminLayout, useAdminApi } from '@/components/AdminLayout';

const CATEGORY_STYLE = { bug: 'text-red-400', idea: 'text-[#EF9F27]', other: 'text-gray-300' };

export default function AdminFeedbackPage() {
  const api = useAdminApi();
  const [data, setData] = useState({ entries: [], total: 0, new: 0 });

  const load = useCallback(() => {
    if (!api) return;
    api.get('/feedback').then((r) => setData(r.data)).catch(() => toast.error('Failed to load feedback'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/feedback/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-feedback-page">
        <h1 className="text-2xl font-bold font-['Outfit']">Beta feedback</h1>
        <p className="text-gray-400" data-testid="admin-feedback-total">{data.total} messages · {data.new} new</p>
      </div>
      <div className="space-y-3">
        {data.entries.map((f) => (
          <div key={f.id} className="p-4 rounded-lg border border-white/10 bg-[#0a1a12]" data-testid={`admin-feedback-row-${f.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-3 text-xs">
                  <span className={`uppercase font-semibold ${CATEGORY_STYLE[f.category]}`}>{f.category}</span>
                  <span className="text-gray-500">{f.portal} · {f.page || '-'}</span>
                  {f.rating && <span className="flex items-center gap-0.5 text-[#EF9F27]"><Star className="w-3 h-3 fill-[#EF9F27]" />{f.rating}</span>}
                </div>
                <p className="text-white whitespace-pre-wrap">{f.message}</p>
                <p className="text-xs text-gray-500">
                  {f.user_name || 'Anonymous'}{f.email ? ` · ${f.email}` : ''} · {new Date(f.created_at).toLocaleString('en-IN')}
                </p>
              </div>
              <Select value={f.status} onValueChange={(v) => setStatus(f.id, v)}>
                <SelectTrigger className="w-32 bg-transparent border-white/20 text-white" data-testid={`admin-feedback-status-${f.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {data.entries.length === 0 && <p className="text-gray-500">No feedback yet.</p>}
      </div>
    </AdminLayout>
  );
}
